import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLine, FadeUp, SectionTag } from "../components/site/Reveal";
import WorkflowPipeline from "../components/site/WorkflowPipeline";
import TradesMarquee from "../components/site/Marquee";
import ArtifactShowcase, { InvoiceCard } from "../components/site/ArtifactCards";
import LeakCalculator from "./LeakCalculator";
import { useBrand } from "../lib/brand";
import ExplainerSection, { WorkflowBoard } from "../components/site/ExplainerSection";

const PILLARS = [
  { code: "MOD-01", title: "Lead Capture", line: "One intake channel. Zero lost leads." },
  { code: "MOD-02", title: "Estimates", line: "Quotes in minutes, priced from real numbers." },
  { code: "MOD-03", title: "Scheduling", line: "A calendar that runs the week for you." },
  { code: "MOD-04", title: "Customer Records", line: "Every customer, one clean record." },
  { code: "MOD-05", title: "Invoicing", line: "Same-day invoices, every job." },
  { code: "MOD-06", title: "Payments", line: "Digital collection. No chasing checks." },
  { code: "MOD-07", title: "Follow-Up", line: "Reviews, rebooks, referrals on autopilot." },
];

const CHAOS = [
  "Leads in voicemail, texts, and a notebook in the truck",
  "Quotes written at midnight — or forgotten entirely",
  "Schedule living in your head, double-bookings weekly",
  "Invoices sent days late, checks chased for weeks",
  "Follow-up? Only when things go quiet",
];
const SYSTEM = [
  "Every lead logged with an instant auto-reply",
  "Quotes out in under an hour from a template",
  "One calendar — confirmed, buffered, routed",
  "Invoice sent before the truck leaves the driveway",
  "Review and rebook requests fire automatically",
];

export default function Home() {
  const heroRef = useRef(null);
  const brand = useBrand();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 20 });
  const sy = useSpring(my, { stiffness: 50, damping: 20 });
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const onMouse = (e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 24);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 24);
  };

  return (
    <div data-testid="home-page" className="bg-navy">
      {/* ============ KINETIC HERO ============ */}
      <section
        ref={heroRef}
        onMouseMove={onMouse}
        className="relative min-h-screen flex flex-col justify-center overflow-hidden blueprint-grid grain pt-16"
      >
        <motion.div
          style={{ x: sx, y: sy }}
          className="absolute -right-32 top-24 w-[520px] h-[520px] rounded-full bg-safety/10 blur-3xl pointer-events-none"
        />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-12 items-center py-24">
          <div className="lg:col-span-7 relative z-10">
            <FadeUp y={12}>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-safety mb-8" data-testid="hero-kicker">
                [{brand.business_name} // The Back Office Blueprint]
              </p>
            </FadeUp>
            <h1 className="font-display font-extrabold tracking-tight text-white text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.02]">
              <MaskedLine delay={0.15}>Systems for</MaskedLine>
              <MaskedLine delay={0.3}>businesses that</MaskedLine>
              <MaskedLine delay={0.45}><span className="text-safety">do the work.</span></MaskedLine>
            </h1>
            <FadeUp delay={0.85} className="mt-8 max-w-xl">
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                You already have paying customers. What you need is a system to keep up.
                The Back Office Blueprint helps service-business owners organize leads,
                estimates, scheduling, customer records, invoicing, payments, and
                follow-up—so the business is not dependent on you remembering every detail.
              </p>
            </FadeUp>
            <FadeUp delay={1.0} className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/apply"
                data-testid="hero-apply-btn"
                className="bg-safety hover:bg-safety-hover text-white font-semibold px-8 py-4 flex items-center gap-2 transition-colors group"
              >
                Apply for the Founding Cohort
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="hero-how-btn"
                className="border border-white/20 hover:border-safety text-white font-semibold px-8 py-4 transition-colors"
              >
                See How It Works
              </Link>
            </FadeUp>
            <FadeUp delay={1.15} className="mt-12 flex gap-8">
              {["7 modules", "Guided build", "You own the system"].map((t) => (
                <span key={t} className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{t}</span>
              ))}
            </FadeUp>
          </div>

          <motion.div style={{ y: imgY }} className="lg:col-span-5 relative hidden lg:block">
            <FadeUp delay={0.6} y={40}>
              <WorkflowBoard />
            </FadeUp>
          </motion.div>
        </div>
        <FadeUp delay={1.4} className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-2 text-slate-500">
          <ArrowDown size={14} className="animate-bounce" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Scroll the manifesto</span>
        </FadeUp>
      </section>

      <TradesMarquee />

      <ExplainerSection />

      {/* ============ CHAPTER 01 — THE PROBLEM ============ */}
      <section className="bg-bone text-navy relative blueprint-grid-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <FadeUp>
                <SectionTag>Chapter 01 // The Problem</SectionTag>
                <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
                  The work isn't the hard part anymore.
                </h2>
                <p className="text-slate-600 mt-6 leading-relaxed">
                  You can clean, wash, fix, paint, haul. What eats your nights is everything
                  around the work — the leads, the quotes, the calendar, the money. That
                  pile is a system problem, and it has a system fix.
                </p>
              </FadeUp>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
              <FadeUp delay={0.1}>
                <div className="bg-navy text-white p-7 h-full grain relative" data-testid="chaos-card">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-5">[MODE: CHAOS]</p>
                  <ul className="space-y-4">
                    {CHAOS.map((c, i) => (
                      <li key={c} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                        <span className="font-mono text-slate-600 shrink-0">0{i + 1}</span>{c}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeUp>
              <FadeUp delay={0.25}>
                <div className="bg-white border-2 border-safety p-7 h-full relative" data-testid="system-card">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-safety mb-5">[MODE: SYSTEM]</p>
                  <ul className="space-y-4">
                    {SYSTEM.map((c, i) => (
                      <li key={c} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                        <span className="font-mono text-safety shrink-0">0{i + 1}</span>{c}
                      </li>
                    ))}
                  </ul>
                  <span className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-widest bg-safety text-white px-2 py-1">After Blueprint</span>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CHAPTER 02 — THE 7 PILLARS ============ */}
      <section className="bg-navy relative grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <FadeUp className="max-w-3xl">
            <SectionTag dark>Chapter 02 // The Blueprint</SectionTag>
            <h2 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
              Seven systems. One back office. Built by you, with a guide.
            </h2>
          </FadeUp>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-14">
            {PILLARS.map((p, i) => (
              <FadeUp key={p.code} delay={i * 0.06} className="h-full">
                <Link
                  to="/program"
                  data-testid={`pillar-card-${p.code.toLowerCase()}`}
                  className="block bg-navy hover:bg-charcoal p-7 h-full group transition-colors"
                >
                  <p className="font-mono text-[11px] tracking-[0.2em] text-safety">[{p.code}]</p>
                  <h3 className="font-display font-bold text-white text-xl mt-3 group-hover:text-safety transition-colors">{p.title}</h3>
                  <p className="text-slate-400 text-sm mt-2 leading-relaxed">{p.line}</p>
                </Link>
              </FadeUp>
            ))}
            <FadeUp delay={0.45} className="h-full">
              <Link
                to="/apply"
                data-testid="pillar-card-apply"
                className="block bg-safety hover:bg-safety-hover p-7 h-full transition-colors"
              >
                <p className="font-mono text-[11px] tracking-[0.2em] text-navy">[YOUR MOVE]</p>
                <h3 className="font-display font-bold text-white text-xl mt-3">Apply for the next cohort</h3>
                <p className="text-white/80 text-sm mt-2 flex items-center gap-2">Start the application <ArrowRight size={15} /></p>
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ============ GUIDED, NOT DONE-FOR-YOU ============ */}
      <section className="bg-navy border-y border-white/10 relative" data-testid="guided-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <FadeUp className="lg:col-span-5">
            <SectionTag dark>Important // Read This First</SectionTag>
            <h2 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl leading-[1.05] mt-5">
              Guided implementation.<br /><span className="text-safety">Not done-for-you.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-7">
            <p className="text-slate-300 leading-relaxed">
              We don't build your back office for you — we teach you to build it yourself,
              with training, templates, workflows, coaching, and accountability at every
              step. When the cohort ends, the system is yours: no dependency on us, no
              lock-in, and ready to hand to your first hire.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {["You do the building", "We guide every step", "You own the result"].map((t) => (
                <div key={t} className="border border-white/10 bg-charcoal/60 px-4 py-3 font-mono text-xs uppercase tracking-wider text-slate-300">{t}</div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ============ CHAPTER 03 — THE PIPELINE ============ */}
      <section className="bg-bone relative blueprint-grid-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <FadeUp className="lg:col-span-7">
              <SectionTag>Chapter 03 // The Engine</SectionTag>
              <h2 className="font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
                Watch a job move through a real back office.
              </h2>
            </FadeUp>
            <FadeUp delay={0.15} className="lg:col-span-5">
              <p className="text-slate-600 leading-relaxed">
                This is the pipeline you'll build inside the Blueprint. Tap any node —
                this is what "handled" actually looks like.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <WorkflowPipeline dark={false} />
          </FadeUp>
        </div>
      </section>

      {/* ============ CHAPTER 04 — THE LEAK ============ */}
      <section className="bg-navy grain relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-5">
            <SectionTag dark>Chapter 04 // The Cost</SectionTag>
            <h2 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
              Chaos has a price tag. Run yours.
            </h2>
            <p className="text-slate-400 mt-6 leading-relaxed">
              Slow quotes, forgotten follow-ups, late invoices — it doesn't feel like a
              crisis because it leaks a little every day. Slide the numbers to your week.
            </p>
            <div className="mt-10 hidden lg:block max-w-sm">
              <InvoiceCard />
            </div>
          </FadeUp>
          <FadeUp delay={0.2} className="lg:col-span-7">
            <LeakCalculator dark />
          </FadeUp>
        </div>
      </section>

      {/* ============ CHAPTER 05 — ARTIFACTS ============ */}
      <section className="bg-bone relative blueprint-grid-light overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <FadeUp className="max-w-3xl mb-16">
            <SectionTag>Chapter 05 // The Proof</SectionTag>
            <h2 className="font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
              Not software you rent. Artifacts you own.
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              Every module ends with a working artifact installed in your business —
              templates, scripts, routines, and routines that keep running when the
              program ends.
            </p>
          </FadeUp>
          <ArtifactShowcase />
        </div>
      </section>

      {/* ============ WHO THIS IS FOR ============ */}
      <section className="bg-bone relative blueprint-grid-light border-t border-slate-200" data-testid="who-for-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <FadeUp className="max-w-3xl">
            <SectionTag>Fit Check // Who This Is For</SectionTag>
            <h2 className="font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
              Built for owner-operated service businesses.
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              If you do the work yourself — or run a small crew — and the back office
              currently runs on memory and late nights, this was built for you.
            </p>
          </FadeUp>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-slate-300 border border-slate-300 mt-12">
            {["Residential Cleaning", "Commercial Cleaning", "Window Cleaning", "Pressure Washing", "Landscaping", "Maintenance", "Mobile Detailing", "Handyman Services", "Painting", "Junk Removal"].map((t, i) => (
              <FadeUp key={t} delay={i * 0.04} className="h-full">
                <div className="bg-bone hover:bg-white transition-colors p-5 h-full flex flex-col justify-between" data-testid={`trade-tile-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="font-mono text-[10px] tracking-[0.2em] text-safety">[{String(i + 1).padStart(2, "0")}]</p>
                  <p className="font-display font-bold text-navy mt-2">{t}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="text-slate-500 text-sm mt-6">And similar hands-on service businesses. Not sure you fit? Apply — we'll tell you straight.</p>
          </FadeUp>
        </div>
      </section>

      {/* ============ FOUNDING COHORT PRICING ============ */}
      <section className="bg-navy grain relative" data-testid="founding-pricing-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-7">
            <SectionTag dark>Enrollment // Founding Cohort</SectionTag>
            <h2 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
              Founding Cohort pricing.
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed mt-6 max-w-xl" data-testid="founding-pricing-copy">
              $497 paid in full or 2 payments of $275. No payment is required to apply.
              Accepted applicants receive enrollment instructions.
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5">
            <div className="border border-white/10 bg-charcoal p-8">
              <div className="flex justify-between items-center border-b border-white/10 pb-4">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">[PAY IN FULL]</span>
                <span className="font-display font-extrabold text-white text-3xl">$497</span>
              </div>
              <div className="flex justify-between items-center pt-4">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">[PAYMENT PLAN]</span>
                <span className="font-display font-extrabold text-white text-3xl">2 × $275</span>
              </div>
              <Link
                to="/apply"
                data-testid="founding-pricing-apply-btn"
                className="mt-8 bg-safety hover:bg-safety-hover text-white font-semibold px-6 py-4 flex items-center justify-center gap-2 transition-colors w-full"
              >
                Apply for the Founding Cohort <ArrowRight size={16} />
              </Link>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-4 text-center">
                14-day fit guarantee // Test-mode checkout during founding cohort
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="bg-safety relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] blueprint-grid-light" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 text-center relative">
          <FadeUp>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-navy mb-6">[Final chapter // Yours to write]</p>
            <h2 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.03]">
              Build the back office<br />your work deserves.
            </h2>
            <p className="text-white/85 max-w-xl mx-auto mt-6 leading-relaxed">
              Cohorts are small on purpose — every application is reviewed by hand.
              If you're doing the work and drowning in the admin, this was built for you.
            </p>
            <Link
              to="/apply"
              data-testid="cta-apply-btn"
              className="inline-flex items-center gap-2 bg-navy hover:bg-charcoal text-white font-semibold px-10 py-4 mt-10 transition-colors group"
            >
              Apply for the Founding Cohort
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
